import { callFunction } from '../edge-functions';
import type { Agent } from '../types';
import type {
  ConnectionDraft,
  ConnectionTestResult,
  ExternalConnection,
} from './types';

export const EXTERNAL_AGENT_SOURCE = 'external_api';

/** `code` when the saved settings haven't passed a test. */
export const NEEDS_TEST = 'needs_test';

/** `code` when the customer's endpoint failed a turn. */
export const ENDPOINT_ERROR = 'endpoint_error';

export const isExternalAgent = (agent?: Pick<Agent, 'source'> | null) =>
  agent?.source === EXTERNAL_AGENT_SOURCE;

/** Runs the connection test on draft settings. */
export const testConnectionDraft = (
  workspaceId: string,
  config: ConnectionDraft,
  secret: string,
  agentId?: string,
) =>
  callFunction<ConnectionTestResult>('external-agents', {
    workspaceId,
    action: 'test',
    config,
    secret,
    agentId,
  });

/** Runs the connection test again on a saved agent's settings. */
export const retestSavedConnection = (workspaceId: string, agentId: string) =>
  callFunction<ConnectionTestResult>('external-agents', {
    workspaceId,
    action: 'test',
    agentId,
  });

export const saveExternalAgent = (
  workspaceId: string,
  params: {
    agentId?: string;
    name: string;
    greeting: string;
    config: ConnectionDraft;
    /** Blank keeps the saved secret when editing. */
    secret: string;
    receipt: string;
  },
) =>
  callFunction<{ agentId: string; name: string }>('external-agents', {
    workspaceId,
    action: 'save',
    ...params,
  });

/** One agent reply from the customer's endpoint. */
export const getExternalAgentReply = ({
  workspaceId,
  simulationId,
  message,
  messageIndex,
  history,
  signal,
}: {
  workspaceId: string;
  simulationId: string;
  message: string;
  messageIndex: number;
  history: Array<{ role: 'user' | 'assistant'; content: string }>;
  signal?: AbortSignal;
}) =>
  callFunction<{ content: string; metadata: Record<string, unknown> }>(
    'external-agent-turn',
    {
      workspaceId,
      simulationId,
      message,
      messageIndex,
      history: history.map(({ role, content }) => ({ role, content })),
    },
    signal,
  );

/** The form's starting point for an existing connection. */
export function draftFromConnection(row: ExternalConnection): ConnectionDraft {
  return {
    format: row.format,
    endpointUrl: row.endpoint_url,
    authType: row.auth_type,
    authHeaderName: row.auth_header_name ?? 'X-API-Key',
    model: row.model ?? '',
    conversationMode: row.conversation_mode,
    sessionIdSource: row.session_id_source ?? 'response',
    sessionResponsePath: row.session_response_path ?? 'conversation_id',
    requestTemplate: row.request_template ?? '',
    replyPath: row.reply_path ?? '',
  };
}
